import { useEffect, useRef, useState } from "react";

function getAudioContextClass(): typeof AudioContext | null {
  return (
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  );
}

export function createHostAudioVolumeProcessor() {
  let audioContext: AudioContext | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let gainNode: GainNode | null = null;
  let outputTrack: MediaStreamTrack | null = null;
  let volume = 100;

  const disconnectNodes = () => {
    source?.disconnect();
    gainNode?.disconnect();
    source = null;
    gainNode = null;
    outputTrack = null;
  };

  return {
    setSourceTrack(track: MediaStreamTrack | null): MediaStreamTrack | null {
      disconnectNodes();
      if (!track) return null;

      const AudioCtx = getAudioContextClass();
      if (!AudioCtx) return track;

      if (!audioContext) {
        audioContext = new AudioCtx();
      }

      source = audioContext.createMediaStreamSource(new MediaStream([track]));
      gainNode = audioContext.createGain();
      const destination = audioContext.createMediaStreamDestination();

      source.connect(gainNode);
      gainNode.connect(destination);
      gainNode.gain.value = volume / 100;
      outputTrack = destination.stream.getAudioTracks()[0] ?? null;

      if (audioContext.state === "suspended") {
        void audioContext.resume();
      }

      return outputTrack ?? track;
    },
    setVolume(nextVolume: number) {
      volume = nextVolume;
      if (gainNode) {
        gainNode.gain.value = volume / 100;
      }
    },
    getOutputTrack(): MediaStreamTrack | null {
      return outputTrack;
    },
    dispose() {
      disconnectNodes();
      void audioContext?.close();
      audioContext = null;
    },
  };
}

export type HostAudioVolumeProcessor = ReturnType<typeof createHostAudioVolumeProcessor>;

export function useHostAudioVolume({
  processor,
  audioTrack,
  volume,
}: {
  processor: HostAudioVolumeProcessor;
  audioTrack: MediaStreamTrack | null;
  volume: number;
}) {
  const [processedAudioTrack, setProcessedAudioTrack] = useState<MediaStreamTrack | null>(null);

  useEffect(() => {
    setProcessedAudioTrack(processor.setSourceTrack(audioTrack));
  }, [processor, audioTrack]);

  useEffect(() => {
    processor.setVolume(volume);
  }, [processor, volume]);

  useEffect(() => {
    return () => {
      processor.dispose();
    };
  }, [processor]);

  return processedAudioTrack ?? audioTrack;
}
